/**
 * useLicenseUpdates Hook (HA-111)
 * Polls for license changes and triggers appropriate actions
 *
 * HA-221: Fixed excessive polling caused by unstable callback references
 * in useEffect dependencies. Now uses refs to maintain stable references.
 */

import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  checkLicenseStatus,
  getLicenseTimestamp,
  LicenseStatusCheckResponse,
} from '../api/licenseAPI';
import { useLicense } from '../context/LicenseContext';
import { isAuthError } from '../utils/authErrorHandler';
import isAuthenticated from '../utils/authUtils';

export interface LicenseUpdateEvent {
  type: 'UPGRADE' | 'DOWNGRADE' | 'REFRESH_REQUIRED' | 'FEATURE_CHANGE';
  oldTier?: string;
  newTier: string;
  changedFeatures?: string[];
  timestamp: Date;
}

export interface UseLicenseUpdatesOptions {
  /**
   * Polling interval in milliseconds (default: 60000 = 1 minute)
   */
  pollingInterval?: number;

  /**
   * Whether to enable polling (default: true)
   */
  enabled?: boolean;

  /**
   * Callback when license changes are detected
   */
  onLicenseChange?: (event: LicenseUpdateEvent) => void;

  /**
   * Callback when an upgrade is detected
   */
  onUpgrade?: (oldTier: string, newTier: string) => void;

  /**
   * Callback when a downgrade is detected
   */
  onDowngrade?: (oldTier: string, newTier: string) => void;

  /**
   * Callback when token refresh is needed
   */
  onRefreshRequired?: () => void;
}

export interface UseLicenseUpdatesResult {
  /**
   * Whether a license update check is in progress
   */
  isChecking: boolean;

  /**
   * Last check result
   */
  lastCheckResult: LicenseStatusCheckResponse | null;

  /**
   * Last error from check
   */
  lastError: Error | null;

  /**
   * Timestamp of last successful check
   */
  lastCheckAt: Date | null;

  /**
   * Whether the license has pending changes that need token refresh
   */
  hasPendingChanges: boolean;

  /**
   * Manually trigger a license check
   */
  checkNow: () => Promise<void>;

  /**
   * Start polling
   */
  startPolling: () => void;

  /**
   * Stop polling
   */
  stopPolling: () => void;
}

const DEFAULT_POLLING_INTERVAL = 60000; // 1 minute

/**
 * Hook for detecting and responding to license changes in real-time
 *
 * HA-221: This hook uses refs for callbacks and state to prevent excessive
 * re-renders and polling. The polling interval useEffect only depends on
 * isPolling and pollingInterval to avoid recreating intervals unnecessarily.
 */
export const useLicenseUpdates = (
  options: UseLicenseUpdatesOptions = {}
): UseLicenseUpdatesResult => {
  const {
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    enabled = true,
    onLicenseChange,
    onUpgrade,
    onDowngrade,
    onRefreshRequired,
  } = options;

  const license = useLicense();

  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckResult, setLastCheckResult] = useState<LicenseStatusCheckResponse | null>(null);
  const [lastError, setLastError] = useState<Error | null>(null);
  const [lastCheckAt, setLastCheckAt] = useState<Date | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isPolling, setIsPolling] = useState(enabled);

  // Track last known values for change detection
  const lastKnownTierRef = useRef<string | undefined>(license.tier ?? undefined);
  const lastKnownUpdateRef = useRef<string | undefined>(undefined);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // HA-221: Use refs for callbacks to maintain stable references and prevent
  // excessive polling caused by callback identity changes
  const onLicenseChangeRef = useRef(onLicenseChange);
  const onUpgradeRef = useRef(onUpgrade);
  const onDowngradeRef = useRef(onDowngrade);
  const onRefreshRequiredRef = useRef(onRefreshRequired);
  const licenseRefreshRef = useRef(license.refresh);
  const isCheckingRef = useRef(isChecking);

  // Keep refs up to date
  useEffect(() => {
    onLicenseChangeRef.current = onLicenseChange;
  }, [onLicenseChange]);

  useEffect(() => {
    onUpgradeRef.current = onUpgrade;
  }, [onUpgrade]);

  useEffect(() => {
    onDowngradeRef.current = onDowngrade;
  }, [onDowngrade]);

  useEffect(() => {
    onRefreshRequiredRef.current = onRefreshRequired;
  }, [onRefreshRequired]);

  useEffect(() => {
    licenseRefreshRef.current = license.refresh;
  }, [license.refresh]);

  useEffect(() => {
    isCheckingRef.current = isChecking;
  }, [isChecking]);

  // HA-278: Keep isPolling in sync with the `enabled` prop. Without
  // this, the useState(enabled) initializer is sticky — `enabled`
  // flipping false (e.g. once currentUser settles as Client and
  // UpgradePromptContext disables polling) doesn't actually stop
  // the interval, so /api/license/check keeps firing 403s.
  useEffect(() => {
    setIsPolling(enabled);
  }, [enabled]);

  // Update refs when license context changes
  useEffect(() => {
    if (license.tier) {
      lastKnownTierRef.current = license.tier;
    }
  }, [license.tier]);

  // Define stopPolling before checkNow since checkNow depends on it (HA-208)
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  // HA-221: checkNow uses refs instead of direct dependencies to maintain
  // a stable function reference and prevent the polling useEffect from
  // restarting on every callback/license change
  const checkNow = useCallback(async () => {
    if (isCheckingRef.current) return;
    // HA-243: Don't fire license check when not authenticated — avoids 401 on page load
    if (!isAuthenticated()) return;

    setIsChecking(true);
    setLastError(null);

    try {
      const result = await checkLicenseStatus(lastKnownUpdateRef.current, lastKnownTierRef.current);

      setLastCheckResult(result);
      setLastCheckAt(new Date());

      // Update last known update timestamp
      if (result.updatedAtUtc) {
        lastKnownUpdateRef.current = result.updatedAtUtc;
      }

      if (result.hasChanged) {
        setHasPendingChanges(true);

        // Determine the type of change
        let eventType: LicenseUpdateEvent['type'] = 'REFRESH_REQUIRED';

        if (result.action === 'SHOW_UPGRADE_SUCCESS') {
          eventType = 'UPGRADE';
        } else if (result.action === 'SHOW_DOWNGRADE_NOTICE') {
          eventType = 'DOWNGRADE';
        } else if (result.changedFeatures && result.changedFeatures.length > 0) {
          eventType = 'FEATURE_CHANGE';
        }

        const event: LicenseUpdateEvent = {
          type: eventType,
          oldTier: lastKnownTierRef.current,
          newTier: result.tier,
          changedFeatures: result.changedFeatures,
          timestamp: new Date(),
        };

        // Update last known tier
        lastKnownTierRef.current = result.tier;

        // Trigger callbacks via refs (HA-221)
        onLicenseChangeRef.current?.(event);

        if (eventType === 'UPGRADE' && event.oldTier) {
          onUpgradeRef.current?.(event.oldTier, result.tier);
        } else if (eventType === 'DOWNGRADE' && event.oldTier) {
          onDowngradeRef.current?.(event.oldTier, result.tier);
        }

        if (result.action === 'REFRESH_TOKEN') {
          onRefreshRequiredRef.current?.();
        }

        // Refresh the license context via ref (HA-221)
        licenseRefreshRef.current();
      }
    } catch (error) {
      setLastError(error instanceof Error ? error : new Error(String(error)));

      // HA-208: Stop polling on authentication errors to prevent infinite loops
      // The auth error handler will redirect to login, so we should stop polling
      if (isAuthError(error)) {
        console.warn('License check: Authentication error detected, stopping polling');
        stopPolling();
      } else if (isAxiosError(error) && error.response?.status === 403) {
        // No practice behind this login. That won't change mid-session, so asking
        // again every minute only repeats the 403.
        console.warn('License check: no practice for this user, stopping polling');
        stopPolling();
      } else {
        console.warn('License update check failed:', error);
      }
    } finally {
      setIsChecking(false);
    }
  }, [stopPolling]); // HA-221: Only depend on stopPolling, use refs for everything else

  // HA-221: Set up polling interval - only depends on isPolling and pollingInterval
  // to prevent excessive interval recreation
  useEffect(() => {
    if (!isPolling || pollingInterval <= 0) {
      return;
    }

    // Do an initial check
    checkNow();

    // Set up polling
    intervalRef.current = setInterval(() => {
      checkNow();
    }, pollingInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, pollingInterval, checkNow]);

  // Clear pending changes when license context refreshes
  useEffect(() => {
    if (hasPendingChanges && license.tier === lastKnownTierRef.current) {
      setHasPendingChanges(false);
    }
  }, [license.tier, hasPendingChanges]);

  return {
    isChecking,
    lastCheckResult,
    lastError,
    lastCheckAt,
    hasPendingChanges,
    checkNow,
    startPolling,
    stopPolling,
  };
};

/**
 * Lightweight hook for just getting the license timestamp
 * Useful for quick freshness checks without full status comparison
 */
export const useLicenseTimestamp = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;
  const [timestamp, setTimestamp] = useState<Date | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchTimestamp = useCallback(async () => {
    if (!enabled || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await getLicenseTimestamp();

      setTimestamp(new Date(result.updatedAt));
      setTier(result.tier);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [enabled, isLoading]);

  useEffect(() => {
    if (enabled) {
      fetchTimestamp();
    }
  }, [enabled, fetchTimestamp]);

  return {
    timestamp,
    tier,
    isLoading,
    error,
    refresh: fetchTimestamp,
  };
};

export default useLicenseUpdates;
